import React, { useEffect, useState } from 'react';
import { TranslationService } from '@rocheglobal/component-library-dot-com/src/services/translation/index';
import { StringService as StringHelper } from '@rocheglobal/component-library-dot-com/src/services/string/index';
import { Props } from '../types';
import { blokToComponent } from '../helpers';
import { NavigationService } from '../../services';
import { sleep } from '../../utils/sleep';

declare global {
  interface Window {
    MktoForms2: {
      loadForm: (baseUrl: string, munchkinId: string, formId: number, callback: unknown) => void;
    };
  }
}

type MarketoFormInfo = {
  scriptSourceUrl: string;
  marketoFormId: string;
  marketoUrl: string;
  munchkinId: string;
  formId: number;
} | null;

type FormMessagesTranslation = {
  translations: {
    successfulSubmissionMessage: string;
    errorSubmissionMessage: string;
  }
}

function generateMarketoFormInfo(marketoProperty, marketoUrl, marketoId): MarketoFormInfo {
  const scriptSourceUrl = `${marketoUrl}/js/forms2/js/forms2.min.js`;
  const marketoFormId = `mktoForm_${marketoId}`;
  const munchkinId = marketoProperty;
  const formId = marketoId;

  if ([scriptSourceUrl, marketoFormId, munchkinId, formId]
    .some((variable) => typeof variable === 'undefined')) {
    return null;
  }

  return {
    scriptSourceUrl,
    marketoFormId,
    marketoUrl,
    munchkinId,
    formId,
  };
}

export const Marketo = ({
  blok, slot, getComponent,
}: Props): JSX.Element => {
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  // eslint-disable-next-line max-len
  const [formMessagesTranslation, setFormMessagesTranslation] = useState<FormMessagesTranslation | undefined>(undefined);

  const fetchFormMessagesTranslation = async (): Promise<FormMessagesTranslation> => {
    const translations = {
      translations: {
        successfulSubmissionMessage: await TranslationService.translate('form submitted successfully'),
        errorSubmissionMessage: await TranslationService.translate('an error occurred while submitting the form'),
      },
    };
    return translations;
  };

  const handleSuccessfulFormSubmission = async () => {
    setIsSubmissionSuccessful(true);
    setIsSubmissionError(false);

    const redirectDelay = 1500;
    const isExternalURL = blok?.link?.linktype === 'url';
    const url = blok?.link?.story?.url || blok?.link?.url;

    if (!url || url === '') return;

    if (isExternalURL && !NavigationService.isWhitelistedDomain(url)) return;

    await sleep(redirectDelay);

    window.location.href = isExternalURL ? url : `/${url}`.replace('//', '/');
  };

  const handleFormValidation = (form) => {
    if (!form) {
      setIsLoadingError(true);
      return;
    }

    form.onValidate((isValid: boolean) => {
      if (!isValid) {
        setIsSubmissionError(true);
      }
    });

    form.onSuccess(() => {
      handleSuccessfulFormSubmission();
      return false;
    });
  };

  const loadMarketoForm = (marketoUrl: string, munchkinId: string, formId: number) => {
    if (!window.MktoForms2) {
      setIsSubmissionError(true);
      return;
    }

    window.MktoForms2.loadForm(marketoUrl, munchkinId, formId, (form) => {
      handleFormValidation(form);
    });
  };

  const createScriptAndAppendToBody = (
    scriptSourceUrl, marketoUrl: string, munchkinId: string, formId: number,
  ) => {
    const script = document.createElement('script');
    script.src = scriptSourceUrl;
    script.async = true;
    script.onload = () => loadMarketoForm(marketoUrl, munchkinId, formId);
    document.body.appendChild(script);
    return script;
  };

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        setFormMessagesTranslation(await fetchFormMessagesTranslation());
      } catch (translationError) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch translations:', translationError);
      }
    };

    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      marketo_property, marketo_url, form_id,
    } = blok;
    const marketoFormInfo = generateMarketoFormInfo(marketo_property, marketo_url, form_id);
    if (!marketoFormInfo) {
      setIsLoadingError(true);
      return;
    }

    fetchTranslations();

    const {
      scriptSourceUrl, marketoFormId, marketoUrl, munchkinId, formId,
    } = marketoFormInfo;
    setActiveFormId(marketoFormId);

    let script: HTMLScriptElement | null = null;
    if (!window.MktoForms2) {
      script = createScriptAndAppendToBody(scriptSourceUrl, marketoUrl, munchkinId, formId);
    } else {
      loadMarketoForm(marketoUrl, munchkinId, formId);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (script) document.body.removeChild(script);
    };
  }, [blok.embedded_snippet]);

  return (
    <roche-marketo>
      <form
        id={activeFormId || undefined}
        style={{ display: isSubmissionSuccessful ? 'none' : 'block' }} />

      <br></br>

      {isSubmissionSuccessful && (
        blok.success?.[0]
          ? blokToComponent({ blok: blok.success[0], slot, getComponent })
          : <div>{StringHelper.capitalize(formMessagesTranslation
            ?.translations?.successfulSubmissionMessage)}</div>
      )}

      {isSubmissionError && (
        blok?.error[0]
          ? blokToComponent({ blok: blok.error[0], slot, getComponent })
          : <div>{StringHelper.capitalize(formMessagesTranslation
            ?.translations?.errorSubmissionMessage)}</div>
      )}

      {isLoadingError && (
        <roche-editor-alert slot={slot} type='error' text='The embed snippet could not be parsed. Please review the embed code and try again.' />
      )}
    </roche-marketo>
  );
};
